import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useWorkspaceContext } from 'src/providers/WorkspaceProvider/Workspace.context';
import { formatComposerCostLabel, resolveWalletCurrency } from '../Wallet.money';
import {
  WALLET_CURRENCIES,
  type tSendCostEstimate,
  type tWalletCurrencyOrEmpty,
  type tWalletShortfall,
} from '../Wallet.types';
import { WalletService } from '../services/Wallet.service';
import { useWalletContext } from '../Wallet.context';
import { useWalletFlag } from './useWalletFlag';
import {
  UTILITY_WINDOW_CUTOVER_DEFAULT,
  VITE_WAPI_UTILITY_IN_WINDOW_FREE_UNTIL,
} from 'src/constants/envs';

type tCheckBalanceParams = {
  templateType: string;
  recipient: string;
  /**
   * UC-09 / DS-03, fix del bloqueante #1 (ronda 1): el mismo veredicto
   * "gratis en origen" que ya pinta el badge del selector
   * (`isTemplateFreeAtSource`, calculado UNA vez por quien llama y
   * reusado también para el badge y el tracking — nunca recalculado
   * aquí con una copia divergente). Cortocircuita ANTES de cotizar
   * porque el estimador NO conoce la ventana de 24h ni el free entry
   * point (`EstimateRequest` en wallet-service es solo
   * {category, recipients}): preguntarle por un envío gratis en origen
   * puede volver "con costo" y bloquear un mensaje que Meta nunca
   * factura — exactamente lo que UC-09 prohíbe.
   */
  isFreeAtSource: boolean;
};

/**
 * El aviso + bloqueo de saldo insuficiente al enviar una plantilla en el chat
 * 1 a 1 (RET-1806, DS-03 §5).
 *
 * Un solo hook para los DOS caminos en alcance (plantilla sin variables en
 * `<Template/>`, con variables en `<WabTemplatesVariablesAction/>`): la
 * cotización, el veredicto y el estado de bloqueo son EXACTAMENTE el mismo
 * contrato en los dos, y este hook es el único lugar donde vive.
 *
 * GUARDRAIL DURO (criterio de aceptación, no negociable): con el flag
 * `billing_wapi-wallet` apagado O sus flags todavía cargando, `checkBalance`
 * NUNCA llama al estimador — deja pasar el envío exactamente como hoy. Esto
 * gatea ANTES de la petición, no solo al pintar: un workspace con el flag
 * apagado no genera tráfico nuevo hacia el estimador.
 *
 * `isTemplateFreeAtSource` (abajo) es la MISMA verdad que ya pinta el badge
 * 'Gratis'/'Con costo' del selector (Template.component.tsx) — quien llama a
 * `checkBalance` calcula esta condición UNA vez y la reusa para el badge, el
 * tracking y el gate, para que las tres superficies nunca puedan divergir.
 */
export function useWalletSendGate(conversationId?: string) {
  const { currentWorkspaceId } = useWorkspaceContext();
  const { isWalletEnabled, isLoadingWalletFlag } = useWalletFlag();
  const { balance } = useWalletContext();
  const balanceMicros = balance?.amountMicros ?? null;
  const { i18n } = useTranslation();
  const [isBlocked, setIsBlocked] = useState(false);
  const [costLabel, setCostLabel] = useState('');
  /**
   * Lo que falta cubrir, CON su moneda (para el prellenado de recarga, DS-03 +
   * RC-10 — el redondeo entero y el piso viven en el modal).
   *
   * Va como objeto y no como string pelado a proposito: su destino es una
   * comparacion contra el piso del payload de recarga, y ese piso tiene su
   * propia moneda. Ver `tWalletShortfall`.
   */
  const [shortfall, setShortfall] = useState<tWalletShortfall | null>(null);
  /**
   * Última copia CONOCIDA del workspace, actualizada en cada render — a
   * diferencia de `currentWorkspaceId` (capturado por valor en el closure de
   * cada llamada a `checkBalance`), este ref sí refleja el workspace VIGENTE
   * cuando el `await` de la cotización resuelve. Cierra la fuga de dinero
   * cruzado entre tenants (varaCoverageGaps de la ronda 2): si el operador
   * cambia de workspace mientras la cotización viaja, la respuesta llega
   * tarde y para el workspace VIEJO — sin este ref, se pintaría su costo (y
   * se prellenaría su faltante) sobre la conversación del workspace NUEVO.
   */
  const workspaceIdRef = useRef(currentWorkspaceId);
  /**
   * Lo mismo para la CONVERSACIÓN, y hace falta aparte: el ámbito del veredicto
   * es el par (workspace, conversación), pero `workspaceIdRef` solo cierra la
   * mitad de tenant. La otra mitad es la fuga común, no la rara — el operador
   * se va a otro chat del MISMO workspace mientras la cotización viaja, el
   * guardia de workspace pasa porque no cambió, y el costo del contacto
   * anterior se pinta sobre el nuevo.
   */
  const conversationIdRef = useRef(conversationId);

  /**
   * El envío que quedó bloqueado, para volver a cotizarlo cuando llegue saldo.
   * Solo vive mientras la barra está pintada: cualquier veredicto que no
   * bloquea, o el cambio de conversación/workspace, lo suelta.
   */
  const blockedParamsRef = useRef<tCheckBalanceParams | null>(null);

  /**
   * Número de la última llamada a `checkBalance`. Sólo la más reciente pinta:
   * con la re-cotización por saldo pueden viajar varias a la vez (dos subidas
   * seguidas, o un clic en vuelo cuando llega el saldo) y, sin esto, una
   * respuesta vieja e insuficiente que resuelve después de la nueva volvía a
   * pintar la barra — y como el saldo ya no cambia, nada la quitaba.
   */
  const latestCheckRef = useRef(0);

  /**
   * El saldo vigente, para que `checkBalance` sepa con cuál cotizó, y el saldo
   * con el que se pidió la cotización que DEJÓ la barra pintada. La
   * re-cotización compara contra éste y no contra "el último saldo visto": si
   * el saldo sube mientras viaja un clic todavía sin barra, la respuesta vieja
   * bloquea con un saldo que ya no existe, y comparar contra el último visto
   * no vería ninguna subida que reintentar.
   */
  const balanceMicrosRef = useRef(balanceMicros);
  useEffect(() => {
    balanceMicrosRef.current = balanceMicros;
  }, [balanceMicros]);
  const blockedAtBalanceRef = useRef<string | null>(null);

  useEffect(() => {
    workspaceIdRef.current = currentWorkspaceId;
    conversationIdRef.current = conversationId;
  }, [currentWorkspaceId, conversationId]);

  /**
   * EL VEREDICTO TIENE ÁMBITO: es sobre ESTE envío, a ESTE contacto, en ESTE
   * workspace. Al cambiar cualquiera de los dos deja de aplicar y se suelta.
   *
   * Sin esto la barra se convierte en el «banner fijo» que DS-03 prohíbe
   * explícitamente: la fila no se desmonta al cambiar de conversación
   * (`<Template key={template_id}>` se llavea por plantilla y `selectedAction`
   * no se resetea con el chat), así que un aviso sobre el envío a un contacto
   * se quedaba pintado —y bloqueando— encima del siguiente. Con free entry
   * point abierto en el chat nuevo, el badge de la MISMA fila decía «Gratis»
   * mientras la barra decía que no hay saldo.
   *
   * Es el mismo criterio que el compositor ya aplica cuatro líneas más arriba
   * para el modo de intervención (MessageBar.content.tsx): «Intervention is
   * per-chat: leaving the chat exits the mode, otherwise the open composer +
   * paused banner leak into whatever chat is selected next».
   *
   * `workspaceIdRef`, arriba, cubre una fuga DISTINTA y no sustituye a esto:
   * ahí se descarta una respuesta EN VUELO que llega tarde y para el workspace
   * viejo; aquí se suelta una barra YA pintada. Cerrar solo una de las dos deja
   * el costo de un tenant en pantalla sobre otro.
   */
  useEffect(() => {
    setIsBlocked(false);
    setCostLabel('');
    setShortfall(null);
    blockedParamsRef.current = null;
  }, [conversationId, currentWorkspaceId]);

  /**
   * Cotiza y decide si el envío puede seguir.
   *
   * Devuelve `true` cuando el envío puede continuar (flag apagado/cargando,
   * gratis en origen, cotización caída o imposible de resolver, cotizado en
   * cero, o saldo suficiente) y `false` cuando hay que bloquear y mostrar la
   * barra — en ese caso deja `costLabel` y `shortfall` listos para pintarla.
   */
  async function checkBalance(params: tCheckBalanceParams): Promise<boolean> {
    if (!isWalletEnabled || isLoadingWalletFlag || !currentWorkspaceId) {
      return true;
    }

    latestCheckRef.current += 1;
    const checkId = latestCheckRef.current;
    const quotedAtBalance = balanceMicrosRef.current;

    if (params.isFreeAtSource) {
      setIsBlocked(false);
      blockedParamsRef.current = null;
      return true;
    }

    const requestedWorkspaceId = currentWorkspaceId;
    const requestedConversationId = conversationId;

    try {
      const estimate = await WalletService.estimateSendCost(
        requestedWorkspaceId,
        {
          category: templateTypeToWalletCategory(params.templateType),
          recipients: [params.recipient],
        },
      );

      if (
        workspaceIdRef.current !== requestedWorkspaceId ||
        conversationIdRef.current !== requestedConversationId
      ) {
        // La cotización llegó tarde: es de OTRO workspace o de OTRA
        // conversación — ver las notas de los dos refs, arriba. No se pinta
        // nada con ella: ni se bloquea, ni se limpia un bloqueo que sí
        // pertenezca al par vigente.
        return true;
      }

      // DS-03 / UC-09: el discriminador de "no se pudo cotizar" es
      // `unresolvedRecipients`, NUNCA `total === 0` — un total en 0 con
      // destinatario resuelto es gratis en origen, no una cotización
      // imposible. Cotización imposible ⇒ no se bloquea ni se avisa (el gate
      // real vive en el backend, RET-1707).
      const canQuote = estimate.unresolvedRecipients.length === 0;
      const balanceAfterMicros = BigInt(estimate.balanceAfter.amountMicros);
      // RET-2079 (D-4): un total de $0 EXACTO nunca bloquea, aunque el saldo sea
      // negativo (DS-18, DS-21) — con saldo < 0 un cero da `balanceAfter < 0`, y
      // sin esta condición la barra frenaba un envío que no cuesta. Sólo con
      // cotización RESUELTA (`canQuote`); 1 micro SÍ bloquea. Mismo predicado
      // que el guardia del compositor (`useComposerServiceSeal`). Las
      // plantillas no leen la franja (D-6, UC-11): ésa es sólo del texto libre
      // de servicio.
      const insufficient =
        canQuote && balanceAfterMicros < 0n && !isResolvedZeroQuote(estimate);

      if (checkId !== latestCheckRef.current) {
        // Una llamada más nueva ya decide qué se pinta. Ésta sólo le contesta
        // a quien la pidió, con su propio veredicto, sin tocar la barra.
        return !insufficient;
      }

      if (!insufficient) {
        setIsBlocked(false);
        blockedParamsRef.current = null;
        // Sin bloqueo no hay faltante: dejarlo puesto seria dejar un monto que
        // el CTA de una apertura posterior podria prellenar sin motivo.
        setShortfall(null);
        return true;
      }

      // RET-1806, fix del bloqueante de arquitectura de la ronda 2:
      // `estimate.currency` es el string ABIERTO del contrato del estimador
      // (Wallet.types.ts, `tSendCostEstimate`) — el mismo caso que
      // `WalletService.parseWalletBalance` ya valida para el saldo. NO se
      // reimplementa un segundo resolvedor de moneda aquí: se normaliza con
      // el MISMO criterio (pertenencia a `WALLET_CURRENCIES`) y se delega la
      // decisión "sin moneda válida" a `resolveWalletCurrency`
      // (Wallet.money.ts), la única función que sabe qué hacer con eso — su
      // contrato es explícito: "Callers must not substitute a default —
      // printing 'US$0.00' for a workspace that bills in MXN is a wrong
      // number." Un default local (el `'USD'` de la ronda 2) es exactamente
      // ese error, y además le faltaba el `.toUpperCase()` que la fuente
      // única sí aplica.
      const rawCurrency = estimate.currency as tWalletCurrencyOrEmpty;
      const walletCurrency: tWalletCurrencyOrEmpty = WALLET_CURRENCIES.includes(
        rawCurrency as never,
      )
        ? rawCurrency
        : '';
      const currency = resolveWalletCurrency(walletCurrency);

      if (!currency) {
        // Sin moneda resuelta no se pinta dinero (Wallet.money.ts, contrato):
        // no bloquear es más seguro que arriesgar una cifra en la moneda
        // equivocada. Hoy es inalcanzable — wallet-service normaliza a
        // mayúsculas y falla con ErrUnknownCurrency ante cualquier otra
        // cosa— pero el llamador no debe confiar en esa garantía ajena.
        setIsBlocked(false);
        blockedParamsRef.current = null;
        return true;
      }

      /*
        RET-2356 · `DS-25`: el costo de una plantilla es el precio de UN
        mensaje, y se lee igual que la columna de la lista que el operador
        tiene al lado: cuatro decimales, hacia arriba, nunca menos de lo que
        se descuenta. A centavos, una utility a Colombia (US$0.0008) se leía
        «$0.00» en la barra y «$0.0008» en la columna. El faltante de abajo
        no cambia: sale de los micros exactos.
      */
      setCostLabel(
        formatComposerCostLabel({
          amountMicros: estimate.total.amountMicros,
          currency,
          locale: i18n.language,
        }),
      );
      setShortfall({
        amountMicros: (-balanceAfterMicros).toString(),
        // La MISMA moneda ya resuelta arriba para la etiqueta del costo: el
        // faltante sale de `balanceAfter`, que viene en esa moneda.
        currency,
      });
      setIsBlocked(true);
      blockedParamsRef.current = params;
      blockedAtBalanceRef.current = quotedAtBalance;

      return false;
    } catch {
      // D3: la cotización falló (red/500). Este ticket solo AVISA cuando hay
      // veredicto; sin veredicto no bloquea ni avisa.
      return true;
    }
  }

  /**
   * LA RECARGA DESTRABA SIN OTRO CLIC. La barra trae su propio CTA de recarga;
   * el cliente recargaba, el saldo se acreditaba, y la barra y el botón
   * atenuado seguían ahí porque el veredicto sólo se recalculaba al volver a
   * pulsar «Enviar» (Template.component.tsx, `sendTemplate`).
   *
   * Se escucha el SALDO del `WalletProvider`, no el cierre del modal: el
   * provider ya lo relee tras el push `wallet.balance.updated` (ADR-013) y la
   * espera de asentamiento, así que esto cubre cualquier forma de recargar
   * —el CTA, otra pestaña, otro Owner— y sólo dispara cuando el dinero llegó
   * al ledger. Mientras el cobro se asienta la barra sigue igual (sin estado
   * «acreditando…», misma decisión que ADR-013).
   *
   * Vuelve a COTIZAR, no compara contra el costo guardado: la cotización sigue
   * siendo la autoridad. Sólo cuando el saldo SUBE (bajar no puede destrabar)
   * y sólo con la barra pintada. Nunca envía: el envío sigue esperando el clic.
   */
  const checkBalanceRef = useRef(checkBalance);
  useEffect(() => {
    checkBalanceRef.current = checkBalance;
  });
  useEffect(() => {
    const params = blockedParamsRef.current;
    if (!isBlocked || !params || balanceMicros === null) return;
    // Sin saldo conocido al bloquear (el provider aún no tenía lectura o
    // falló), cualquier valor nuevo cuenta como subida.
    const blockedAt = blockedAtBalanceRef.current;
    if (blockedAt !== null && BigInt(balanceMicros) <= BigInt(blockedAt)) return;
    // Se adelanta antes de cotizar para no repetir la misma petición en cada
    // render; la respuesta lo fija de nuevo si vuelve a bloquear.
    blockedAtBalanceRef.current = balanceMicros;
    void checkBalanceRef.current(params);
  }, [balanceMicros, isBlocked]);

  return { isBlocked, costLabel, shortfall, checkBalance };
}

/**
 * RET-2079 (D-4): ¿la cotización está RESUELTA y su total es exactamente cero?
 *
 * Un cero con destinatario resuelto es un envío que no cuesta, y la falta de
 * saldo no bloquea lo que no cuesta (DS-18). NO es lo mismo que una cotización
 * imposible: con `unresolvedRecipients` no vacío el total es 0 porque no se
 * pudo cotizar, no porque sea gratis, así que esto devuelve `false` y cada
 * guardia sigue decidiendo como antes (no bloquea, el gate real vive en el
 * backend, RET-1707). La regla es cero EXACTO: 1 micro ya cuesta.
 *
 * Vive aquí y la usan los DOS guardias —`checkBalance` (plantillas) y
 * `useComposerServiceSeal` (texto libre)— para que no puedan discrepar sobre
 * qué es un cero.
 */
export function isResolvedZeroQuote(estimate: tSendCostEstimate): boolean {
  return (
    estimate.unresolvedRecipients.length === 0 &&
    BigInt(estimate.total.amountMicros) === 0n
  );
}

/**
 * UC-09: "gratis en origen" — los dos casos donde Meta no cobra y que el
 * estimador de wallet-service NO puede saber, porque su request es
 * estructuralmente {category, recipients} (`EstimateRequest`,
 * cost_estimator.go) sin campo de ventana de 24h ni de free entry point:
 *
 *   - una UTILITY dentro de una conversación ACTIVA (ventana de 24h) —
 *     ESTRICTAMENTE ANTES de `VITE_WAPI_UTILITY_IN_WINDOW_FREE_UNTIL` —
 *     RET-2234, por defecto 2026-10-01T00:00:00Z; en ese instante y después
 *     ya tiene costo, D-6), o
 *   - cualquier plantilla en un chat con free entry point abierto (la ventana
 *     que abre un anuncio click-to-WhatsApp o un CTA de la página de
 *     Facebook). Esta
 *     mitad NO caduca (RET-2234 D-3): sigue gratis a los dos lados del corte,
 *     con cualquier `templateType`.
 *
 * Es la MISMA condición que ya pinta el badge 'Gratis'/'Con costo' del
 * selector (Template.component.tsx) — quien llama a `checkBalance` debe
 * calcularla con esta función y reusar el resultado para el badge, el
 * tracking de analytics Y el gate, nunca con una copia inline: dos copias
 * que un día difieran es exactamente el bloqueante #1 de la ronda 1 (la
 * barra se pintaba, con costo, sobre un envío que el badge de la MISMA fila
 * ya marcaba gratis).
 *
 * La firma NO lleva un `now` obligatorio: el corte se resuelve internamente
 * leyendo el reloj y `VITE_WAPI_UTILITY_IN_WINDOW_FREE_UNTIL` (RET-2234 D-5),
 * para que los dos consumidores del chat (Template.component.tsx,
 * WabTemplatesVariablesAction.hook.tsx) no tengan que saber de fechas — solo
 * de este veredicto. Ambos siguen llamando SIN `at`; no cambian con D-7.
 *
 * D-7 (Fanny, 24-sep, enmienda a D-5): un TERCER consumidor —
 * WabTemplateScheduleAction, el panel de programar envío — pregunta algo
 * distinto: un envío programado dispara en el FUTURO, así que evaluar el
 * reloj de "ahora" respondería la pregunta equivocada (una UTILITY agendada
 * el 28-sep para disparar el 2-oct saldría "Gratis" y llegará cobrada). Por
 * eso el predicado gana un cuarto campo OPCIONAL `at`:
 *   - Ausente o `undefined` ⇒ AHORA, que es lo que ya hacía.
 *   - Con `at`, el reloj real NO cuenta: manda `at`, a los dos lados de D-6.
 *   - Un `at` INVÁLIDO (una `Date` que no parsea) cae al lado SEGURO = ya es
 *     post-corte para la mitad UTILITY+ventana — nunca "gratis por si
 *     acaso", y nunca "cae a ahora" (que con el reloj antes del corte
 *     volvería a decir gratis). `hasFreeEntryPoint` no depende de `at` y
 *     sigue aplicando igual.
 * No re-litiga D-5: la firma sigue siendo un objeto y el corte se sigue
 * resolviendo internamente con el mismo corte configurado (D-2).
 */
export function isTemplateFreeAtSource(params: {
  templateType: string;
  isChatActive: boolean;
  hasFreeEntryPoint: boolean;
  at?: Date;
}): boolean {
  const cutoverInstant = resolveUtilityWindowCutoverInstant();
  const compareInstant = params.at === undefined ? Date.now() : params.at.getTime();

  // RET-2232, D-4.1: el texto libre de servicio (`templateType: 'SERVICE'`,
  // el sello del compositor) entra en la MISMA rama de ventana que UTILITY, y
  // con el MISMO corte — UC-07 dice literal "Service = Utility =
  // Authentication en todos los mercados (misma tarifa)". No es una copia del
  // predicado: es el mismo predicado atendiendo un consumidor más, que es
  // justo lo que D-3 de ese ticket exige.
  const utilityWindowStillFree =
    (params.templateType === 'UTILITY' || params.templateType === 'SERVICE') &&
    params.isChatActive &&
    !Number.isNaN(compareInstant) &&
    compareInstant < cutoverInstant;

  return utilityWindowStillFree || params.hasFreeEntryPoint;
}

/**
 * RET-2234, tarea adicional del maker: blindaje contra un mock PARCIAL de
 * `src/constants/envs` (p. ej. `vi.doMock('src/constants/envs', async
 * (importOriginal) => ({ ...(await importOriginal()),
 * VITE_WAPI_UTILITY_IN_WINDOW_FREE_UNTIL: undefined }))` en una prueba que no
 * le interesa esta fecha pero pisa esa constante). Ese mock deja
 * `VITE_WAPI_UTILITY_IN_WINDOW_FREE_UNTIL` en `undefined` y sin este respaldo
 * `Date.parse(undefined)` da `NaN`. Con `NaN` como corte, CUALQUIER instante
 * lee `< NaN` como `false`: la ventana se leería como YA VENCIDA para
 * siempre, una falla silenciosa del lado de COBRAR (bloquea envíos que hoy
 * son gratis) en vez de fallar visiblemente.
 *
 * El respaldo es `UTILITY_WINDOW_CUTOVER_DEFAULT`, IMPORTADO de `envs.ts` —
 * no un literal repetido aquí. Ese fue el bloqueante arquitectónico de la
 * ronda 2 del checker: dos copias del mismo instante, amarradas solo por un
 * comentario ("Debe coincidir con el DEFAULT"), es exactamente el defecto
 * original de este ticket (un docblock que promete una fecha que el código
 * no codifica) reproducido a un segundo archivo. Un mock PARCIAL como el de
 * arriba preserva el resto del módulo real vía `importOriginal`, así que
 * `UTILITY_WINDOW_CUTOVER_DEFAULT` sigue llegando aquí con su valor real
 * aunque `VITE_WAPI_UTILITY_IN_WINDOW_FREE_UNTIL` esté pisado — un mock
 * verdaderamente PELADO (`() => ({})`) no es el riesgo que esto cubre: revienta
 * en voz alta ("No X export is defined on the mock") antes de llegar aquí,
 * porque decenas de módulos ajenos a este ticket importan `envs.ts`.
 *
 * Mismo contrato que D-2: un valor ausente o que no parsea cae al default
 * documentado, nunca a "no hay corte" ni a la época cero.
 */
function resolveUtilityWindowCutoverInstant(): number {
  const raw = VITE_WAPI_UTILITY_IN_WINDOW_FREE_UNTIL;
  const parsed = raw === undefined || raw === null ? NaN : Date.parse(String(raw));
  return Number.isNaN(parsed)
    ? Date.parse(UTILITY_WINDOW_CUTOVER_DEFAULT)
    : parsed;
}

/**
 * MAPEO CONGELADO por la vara para FOLLOWING/MARKETING → 'marketing' (ver
 * Template.walletGate.test.tsx, B1-BIS, "MAPEO CONGELADO" en su encabezado):
 * el estimador cotiza por categoría de Meta, nunca por template id, y pasar
 * el campo crudo de FOLLOWING lo dejaría sin barra y sin bloqueo
 * (wallet-service lo normaliza con ToLower y contesta ErrNoTariff, un no-op
 * silencioso). El front ya clasifica FOLLOWING como marketing (`isMarketing`,
 * Template.component.tsx); esta función reusa esa misma clasificación.
 *
 * UTILITY | AUTHENTICATION → 'utility' es el fix del bloqueante #2 (ronda
 * 1): UC-07 (unidad-de-cobro.md) dice literal "Service = Utility =
 * Authentication en todos los mercados (misma tarifa)", y el catálogo real
 * de wallet-service (migración `000006_price_catalog`) asigna
 * `authentication` y `service` a la MISMA columna que `utility` — una
 * columna aparte, mucho más cara, es `marketing`. La versión anterior
 * (`templateType === 'UTILITY' ? 'utility' : 'marketing'`) colapsaba
 * AUTHENTICATION en marketing: hasta 15.6x sobreprecio según mercado (México
 * 30500 vs 8500 micros = 3.6x; Colombia 12500 vs 800 = 15.6x), lo que
 * sobre-cotizaba y podía bloquear envíos que el saldo sí cubría.
 *
 * Cualquier otro valor (FOLLOWING, MARKETING, vacío, o un `template_type`
 * desconocido) cae en 'marketing': es el lado seguro del error —
 * sobre-cotizar, nunca sub-cotizar— mientras el catálogo de wallet-service,
 * dueño real de esta escalera categoría × tarifa, no expone una categoría
 * más fina que el front pueda leer directo del template.
 *
 * EXPORTADA para RET-1694: el selector de plantillas pinta el costo de cada
 * renglón ANTES de intentar enviar, y tiene que cotizar con esta misma
 * escalera. Si tuviera su propia copia, la misma plantilla podría mostrarse a
 * un precio en la lista y cobrarse a otro en el gate — que es justo el tipo de
 * divergencia que este mapeo congelado existe para impedir.
 */
export function templateTypeToWalletCategory(templateType: string): string {
  if (templateType === 'UTILITY' || templateType === 'AUTHENTICATION') {
    return 'utility';
  }
  // RET-2232, D-4.2: el texto libre de servicio del compositor cotiza con la
  // misma tarifa que utility/authentication (UC-07) — sembrada en el catálogo
  // real de wallet-service (`000006_price_catalog`) bajo la misma columna.
  // Sin esta clave caería en el 'marketing' de abajo: hasta 15.6x de
  // sobreprecio según mercado, que sobre-cotiza y puede pintar la barra sin
  // saldo sobre un mensaje que el saldo sí cubre. Las cinco entradas de
  // arriba y abajo (UTILITY, AUTHENTICATION, FOLLOWING, MARKETING, default)
  // quedan CONGELADAS: esto sólo agrega una clave nueva.
  if (templateType === 'SERVICE') {
    return 'service';
  }
  return 'marketing';
}
